import React, { useEffect } from "react";
import DateFnsUtils from "@date-io/date-fns";
import {
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormHelperText,
    createMuiTheme,
    ThemeProvider
} from "@material-ui/core";
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import "date-fns";
import { useForm, useFormState } from "react-hook-form";
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { setUnemploymentForm } from "../../application/actions";
import { useAppState } from "../../application/AppState";
import useTranslation from "../../application/translation";
import WizardButtons from './WizardButtons';
import { InputFiles } from "../../application/helpers/InputFiles";
import { getDefaultValue2 } from "../../application/helpers/form";
import { InputRadio } from "../../application/helpers/InputRadio";

import { validString } from "../../application/validation";

export default function FormUnemployment({ handleBack, handleNext }) {
    const [{ formValues, claimDate }, dispatch] = useAppState();
    const translation = useTranslation();

    const datePickerTheme = createMuiTheme({
        overrides: {
            MuiPickersCalendarHeader: {
                dayLabel: {
                    color: '#000000',
                    fontWeight: 600,
                }
            }
        }
    });

    const errorObj = {
        required_error: translation.error_mandatory,
        invalid_type_error: translation.error_invalidentry,
    };
    const requiredObj = { required_error: translation.error_mandatory };
    const errorSelectRequired = translation.select_error;
    const invalidEntry = { message: translation.error_invalidentry }

    const defineRadioButtonSchema = z
        .string()
        .min(1, { message: translation.select_error });

    const formSchema = z.object({
        employmentStartDate: z.date(errorObj),
        employmentStopNotice: z.date(requiredObj),
        employmentStopDate: z.date(errorObj),
        hoursPerWeek: z.number(errorObj).nonnegative({ message: "Värdet måste vara större eller lika med noll" }),
        employmentStopReason: z.string(errorObj).min(1, translation.error_mandatory).refine(validString, invalidEntry),
        typeOfEmployment: z.string(errorObj).min(1, translation.error_mandatory).refine(validString, invalidEntry),
        workFullTime: defineRadioButtonSchema,
        activeSearch: defineRadioButtonSchema,
        selfEmployed: defineRadioButtonSchema,
    }).passthrough();

    const formType = FormUnemployment.name;
    const { handleSubmit, register, setValue, getValues, control, reset } = useForm({
        mode: "onChange",
        defaultValues: {
            employmentStartDate: getDefaultValue2(formValues, formType, "employmentStartDate"),
            workFullTime: getDefaultValue2(formValues, formType, "workFullTime"),
            hoursPerWeek: getDefaultValue2(formValues, formType, "hoursPerWeek"),
            employmentStopNotice: claimDate,
            employmentStopDate: getDefaultValue2(formValues, formType, "employmentStopDate"),
            employmentStopReason: getDefaultValue2(formValues, formType, "employmentStopReason"),
            typeOfEmployment: getDefaultValue2(formValues, formType, "typeOfEmployment"),
            activeSearch: getDefaultValue2(formValues, formType, "activeSearch"),
            selfEmployed: getDefaultValue2(formValues, formType, "selfEmployed"),
            employerCertFile: getDefaultValue2(formValues, formType, "employerCertFile"),
            benefitsCertFile: getDefaultValue2(formValues, formType, "benefitsCertFile"),
            terminationCertFile: getDefaultValue2(formValues, formType, "terminationCertFile"),
            unemploymentCertFile: getDefaultValue2(formValues, formType, "unemploymentCertFile"),
        },
        resolver: zodResolver(formSchema),
    });
    const { errors } = useFormState({ control });

    // useEffect(() => {
    //     reset();
    // }, [reset]);

    const onSubmit = (data) => {
        dispatch(setUnemploymentForm(data));
        handleNext();
    };

    const handleDateByName = (date, name) => {
        setValue(name, date, { shouldValidate: true });
    };

    const values = getValues();

    return (
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <ThemeProvider theme={datePickerTheme}>
                    <Grid container spacing={3}>

                        {/* Employment start */}
                        <Grid item xs={12}>
                            <KeyboardDatePicker
                                id="employmentStartDate"
                                label={translation.unemp_employstart}
                                format={translation.dateformat}
                                margin="normal"
                                inputVariant="outlined"
                                value={values.employmentStartDate || null}
                                onChange={(d) => handleDateByName(d, "employmentStartDate")}
                                KeyboardButtonProps={{ "aria-label": translation.unemp_employstart }}
                                leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                                rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                                error={!!errors.employmentStartDate}
                                helperText={errors.employmentStartDate?.message}
                            />
                        </Grid>

                        {/* Work full time? */}
                        <Grid item xs={12}>
                            {translation.unemp_workfulltime} <br />
                            <InputRadio name="workFullTime" errors={errors} register={register} />
                        </Grid>

                        {/* Hours per week */}
                        <Grid item xs={6}>
                            <TextField
                                id="hoursPerWeek"
                                {...register("hoursPerWeek", {
                                    setValueAs: (v) => (v === "" ? undefined : parseInt(v, 10))
                                })}
                                label={translation.unemp_hoursperweek}
                                variant="outlined"
                                fullWidth
                                type="number"
                                inputProps={{ 'aria-label': translation.unemp_hoursperweek }}
                                error={!!errors.hoursPerWeek}
                                helperText={errors.hoursPerWeek?.message}
                            />
                        </Grid>

                        {/* Stop notice (read-only) */}
                        <Grid item xs={12}>
                            <KeyboardDatePicker
                                id="employmentStopNotice"
                                label={translation.unemp_notice}
                                format={translation.dateformat}
                                margin="normal"
                                inputVariant="outlined"
                                value={claimDate}
                                disabled
                                KeyboardButtonProps={{ "aria-label": translation.unemp_notice }}
                                leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                                rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                            />
                        </Grid>

                        {/* Stop date */}
                        <Grid item xs={12}>
                            <KeyboardDatePicker
                                id="employmentStopDate"
                                label={translation.unemp_terminated}
                                format={translation.dateformat}
                                margin="normal"
                                inputVariant="outlined"
                                value={values.employmentStopDate || null}
                                onChange={(d) => handleDateByName(d, "employmentStopDate")}
                                KeyboardButtonProps={{ "aria-label": translation.unemp_terminated }}
                                leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                                rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                                error={!!errors.employmentStopDate}
                                helperText={errors.employmentStopDate?.message}
                            />
                        </Grid>

                        {/* Stop reason */}
                        <Grid item xs={12}>
                            <TextField
                                id="employmentStopReason"
                                {...register("employmentStopReason")}
                                label={translation.unemp_reason}
                                variant="outlined"
                                fullWidth
                                multiline
                                minRows={2}
                                inputProps={{ 'aria-label': translation.unemp_reason }}
                                error={!!errors.employmentStopReason}
                                helperText={errors.employmentStopReason?.message}
                            />
                        </Grid>

                        {/* Type of employment */}
                        <Grid item xs={12}>
                            <TextField
                                id="typeOfEmployment"
                                {...register("typeOfEmployment")}
                                label={translation.unemp_worktype}
                                variant="outlined"
                                fullWidth
                                multiline
                                minRows={2}
                                inputProps={{ 'aria-label': translation.unemp_worktype }}
                                error={!!errors.typeOfEmployment}
                                helperText={errors.typeOfEmployment?.message}
                            />
                        </Grid>

                        {/* Actively searching? */}
                        <Grid item xs={12}>
                            {translation.unemp_enrolled} <br />
                            <InputRadio name="activeSearch" errors={errors} register={register} />
                        </Grid>

                        {/* Self employed? */}
                        <Grid item xs={12}>
                            {translation.unemp_selfemployed} <br />
                            <InputRadio name="selfEmployed" errors={errors} register={register} />
                        </Grid>

                        {/* Attachments info */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" id="attachmentsHeading">
                                {translation.file_attatchmentsInfo}
                                <Tooltip
                                    title={
                                        <span>
                                            <strong>{translation.file_tooltip1}</strong><br /><br />
                                            {translation.file_tooltip2}<br /><br />
                                            {translation.file_tooltip3}
                                        </span>
                                    }
                                    aria-labelledby="attachmentsHeading"
                                >
                                    <IconButton aria-label={translation.file_tooltip1}>
                                        <HelpOutlineIcon />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                            <Typography variant="body2">
                                {translation.file_attachmentsInfo2}
                            </Typography>
                        </Grid>

                        {/* File inputs */}
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.unemp_file_empcert}</Typography>
                            <InputFiles
                                fieldName="employerCertFile"
                                errors={errors}
                                register={register}
                                formType={formType}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.unemp_file_unempcert}</Typography>
                            <InputFiles
                                fieldName="unemploymentCertFile"
                                errors={errors}
                                register={register}
                                formType={formType}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.unemp_file_terminationcert}</Typography>
                            <InputFiles
                                fieldName="terminationCertFile"
                                errors={errors}
                                register={register}
                                formType={formType}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.unemp_file_payments}</Typography>
                            <InputFiles
                                fieldName="benefitsCertFile"
                                errors={errors}
                                register={register}
                                formType={formType}
                            />
                        </Grid>

                    </Grid>
                </ThemeProvider>
            </MuiPickersUtilsProvider>

            <WizardButtons handleBack={handleBack} isStepValid={true} />
        </form>
    );
}
